// Fetch wrapper with JWT bearer authentication
// VoiceSpectrum.Web.T2 · v12.10.10 · 2026-10-08 · VoiceSpectrum AI (#1401: a 428 confirm_required on ANY call asks "Confirm it's you" and retries once; was v12.10.9.1)
//
// v10.0.10.9 F-02: API host now comes from VITE_API_BASE_URL (set in .env / .env.production).
// In development the value defaults to http://localhost:5000 — see .env.example.
// In production builds the value is "https://api.voicespectrum.ai", set by the deploy
// pipeline's `.env.production` so the SPA running at https://app.voicespectrum.ai posts
// cross-origin to the Api host (and never depends on a same-origin reverse proxy).

import { reportApiSuspect } from './apiHealth';

const API_BASE: string =
  (import.meta as unknown as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL
  ?? 'http://localhost:5000';

// Paths the rest of the app passes start with "/api/...". Concatenate without a trailing slash.
export function buildUrl(path: string): string {
  const trimmedBase = API_BASE.replace(/\/+$/, '');
  const trimmedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${trimmedPath}`;
}

/** v12.10.9 (L12): fired when ANY call (not only /auth/refresh) answers 401 {error:'session_ended'} - the server ended
 *  this session (sign everyone out, disabled, idle, absolute). AuthProvider listens, records why and signs out. */
export const SESSION_ENDED_EVENT = 'vs:session-ended';
function announceSessionEnded(status: number, body: unknown, path: string): void {
  if (status !== 401 || !body || typeof body !== 'object' || path.includes('/auth/')) return;
  const b = body as { error?: string; reason?: string; idleMinutes?: number; absoluteHours?: number };
  if (b.error !== 'session_ended') return;
  try { window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, { detail: { reason: b.reason, idleMinutes: b.idleMinutes, absoluteHours: b.absoluteHours } })); } catch { /* no window */ }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getToken(): string | null {
  return localStorage.getItem('vs_t2_token');
}

export function setToken(token: string): void {
  localStorage.setItem('vs_t2_token', token);
}

export function clearToken(): void {
  localStorage.removeItem('vs_t2_token');
}

// ── v12.10.10 (#1401): "CONFIRM IT'S YOU" IS HANDLED HERE, ONCE, FOR EVERY CALL. ─────────────────────────────
// T2AccessMap marks workflow saves (and terminals, phone numbers, phone activation, recording, platform session)
// Confirm=always. Only the Security screens wrapped their calls in withConfirm(), so on every other screen a lapsed
// confirm window turned Save into a silent 428 - Workflow › Done did nothing at all (found live in Harlem 10/7).
// Now the client asks and retries once, whoever made the call. ConfirmItsYou is loaded lazily: it imports
// securityApi, which imports this file. Cancel throws ConfirmCancelledError (callers already treat it as "no").
// Screens that still wrap in withConfirm() are unaffected: the retried call succeeds, so they never see a 428.
function isConfirmRequiredBody(status: number, body: unknown): boolean {
  if (status !== 428) return false;
  const e = body && typeof body === 'object' ? (body as { error?: string }).error : undefined;
  return e === undefined || e === 'confirm_required';
}
async function askConfirm(): Promise<void> {
  const mod = await import('../components/security/ConfirmItsYou');
  await mod.requestConfirm();
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestInit,
  retried = false,
): Promise<T> {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options?.headers ?? {}),
  };

  // Allow callers to pass either "/api/x" or "x" — buildUrl normalizes.
  const url = path.startsWith('/api') || path.startsWith('api')
    ? buildUrl(path)
    : buildUrl(`/api${path.startsWith('/') ? path : `/${path}`}`);

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include',
      ...options,
    });
  } catch (networkError) {
    reportApiSuspect(); // host unreachable / stopped app pool (CORS-less 503)
    throw networkError;
  }

  if (response.status === 503) reportApiSuspect();

  if (!response.ok) {
    let errBody: unknown;
    try { errBody = await response.json(); } catch { errBody = null; }
    announceSessionEnded(response.status, errBody, path);
    // v12.10.10 (#1401): ask once, retry once. Never on /auth/ (auth/confirm itself answers 4xx on a wrong password).
    if (!retried && !path.includes('/auth/') && isConfirmRequiredBody(response.status, errBody)) {
      await askConfirm();
      return request<T>(method, path, body, options, true);
    }
    throw new ApiError(response.status, `HTTP ${response.status}`, errBody);
  }

  if (response.status === 204) return undefined as T;

  const ct = response.headers.get('Content-Type') ?? '';
  if (ct.includes('application/json')) {
    return response.json() as Promise<T>;
  }
  return response.text() as unknown as T;
}

// v11.8.4.x: multipart/form-data POST (e.g. logo upload). Must NOT set Content-Type so the
// browser adds the multipart boundary; keeps the bearer token.
async function requestForm<T>(path: string, form: FormData, retried = false): Promise<T> {
  const token = getToken();
  const url = path.startsWith('/api') || path.startsWith('api')
    ? buildUrl(path)
    : buildUrl(`/api${path.startsWith('/') ? path : `/${path}`}`);

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
      credentials: 'include',
    });
  } catch (networkError) {
    reportApiSuspect();
    throw networkError;
  }

  if (response.status === 503) reportApiSuspect();

  if (!response.ok) {
    let errBody: unknown;
    try { errBody = await response.json(); } catch { errBody = null; }
    if (!retried && isConfirmRequiredBody(response.status, errBody)) { await askConfirm(); return requestForm<T>(path, form, true); }   // v12.10.10 (#1401)
    throw new ApiError(response.status, `HTTP ${response.status}`, errBody);
  }
  if (response.status === 204) return undefined as T;
  const ct = response.headers.get('Content-Type') ?? '';
  return ct.includes('application/json') ? (response.json() as Promise<T>) : (response.text() as unknown as T);
}

// Resolve a stored media path (e.g. "/media/tenant/12/logo-icon.png") to a full URL on the API
// host. Pass-through for absolute http(s)/data URLs.
export function mediaUrl(path?: string | null): string | undefined {
  if (!path) return undefined;
  if (/^(https?:|data:)/i.test(path)) return path;
  return buildUrl(path);
}

export const api = {
  get:    <T>(path: string, options?: RequestInit) => request<T>('GET', path, undefined, options),
  post:   <T>(path: string, body?: unknown, options?: RequestInit) => request<T>('POST', path, body, options),
  put:    <T>(path: string, body?: unknown, options?: RequestInit) => request<T>('PUT', path, body, options),
  patch:  <T>(path: string, body?: unknown, options?: RequestInit) => request<T>('PATCH', path, body, options),
  delete: <T>(path: string, options?: RequestInit) => request<T>('DELETE', path, undefined, options),
  postForm: <T>(path: string, form: FormData) => requestForm<T>(path, form),
};

export default api;
